import {
  type Edge,
  getConnectedEdges,
  type Node,
  useReactFlow,
  useStore,
  type XYPosition,
} from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'router-v6'
import { v4 as uuidv4 } from 'uuid'
import type { TriggerType } from 'v2/types'
import { logger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'
import type { AgentSettingsFormValues } from '../config/base.config'
import { reactFlowContentSchema } from '../schemas/react-flow-schemas'
import { useFlowStore } from '../store'
import {
  FlowNodeTypes,
  type GenericEdge,
  type GenericNode,
  type NodeDataBase,
  type ReactFlowDataObject,
  type SystemPrompt,
} from '../types'
import { getChannelTypeFromFlowType, getPhloTypeFromStartNodeTrigger } from '../utils'
import { removeOrphanIntents } from '../utils/ai-agent-utils'
import { getImportedAgentSettings } from '../utils/imported-agent-settings'
import { normalizeImportedNodeConfig } from '../utils/imported-node-config'
import { cleanNodesForExport } from '../utils/node-cleanup'
import { refuseSubAccountLockedNodes } from '../utils/sub-account-locks'
import { useFlowId } from './use-flow-id'
import { useNodeLayout } from './use-node-layout'
import useShortcut from './use-shortcut'

// Module-level tracking to prevent duplicate imports (survives React Strict Mode remounts)
let lastProcessedImportId: number | null = null

type PasteMetadata = {
  importedSystemPrompt?: SystemPrompt | null
}

const hasOwn = (value: unknown, key: string) => Object.hasOwn(value ?? {}, key)

export function useCopyPaste<
  NodeType extends Node<NodeDataBase<unknown>> = Node<NodeDataBase<unknown>>,
  EdgeType extends Edge = Edge,
>() {
  const mousePosRef = useRef<XYPosition>({ x: 0, y: 0 })
  const rfDomNode = useStore((state) => state.domNode)
  const isEditing = useFlowStore((state) => state.isEditing)
  const setCurrentFlowType = useFlowStore((state) => state.setCurrentFlowType)

  const { getNodes, getEdges, deleteElements, addNodes, addEdges, setNodes, fitView } =
    useReactFlow<NodeType, EdgeType>()

  const { getLayoutedElements } = useNodeLayout()
  const location = useLocation()
  const { isNew } = useFlowId()

  // Set up the paste buffers to store the copied nodes and edges.
  const [bufferedNodes, setBufferedNodes] = useState([] as NodeType[])
  const [bufferedEdges, setBufferedEdges] = useState([] as EdgeType[])

  const [isDuplicateStartNodeModalOpen, setIsDuplicateStartNodeModalOpen] = useState(false)
  const [conflictedPasteData, setConflictedPasteData] = useState<
    (ReactFlowDataObject<NodeType, EdgeType> & PasteMetadata) | undefined
  >(undefined)

  // initialize the copy/paste hook
  // 1. remove native copy/paste/cut handlers
  // 2. add mouse move handler to keep track of the current mouse position
  useEffect(() => {
    const events = ['cut', 'copy', 'paste']

    if (!rfDomNode) return

    const preventDefault = (e: Event) => {
      const target = e.target as HTMLElement

      // Allow default behavior for text input fields
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.contentEditable === 'true' ||
          target.closest('.ql-editor') || // ReactQuill editor
          target.closest('[contenteditable="true"]') ||
          target.closest('input') ||
          target.closest('textarea'))
      ) {
        return // Don't prevent default for text inputs
      }

      e.preventDefault()
    }

    const onMouseMove = (event: MouseEvent) => {
      mousePosRef.current = {
        x: event.clientX,
        y: event.clientY,
      }
    }

    events.forEach((event) => {
      rfDomNode.addEventListener(event, preventDefault)
    })

    rfDomNode.addEventListener('mousemove', onMouseMove)

    return () => {
      events.forEach((event) => {
        rfDomNode.removeEventListener(event, preventDefault)
      })

      rfDomNode.removeEventListener('mousemove', onMouseMove)
    }
  }, [rfDomNode])

  const applyPastedContent = useCallback(
    async (
      nodesToProcess: NodeType[],
      edgesToProcess: EdgeType[],
      options?: {
        replaceExistingStartNode?: boolean
        replaceAll?: boolean
        importedSystemPrompt?: SystemPrompt | null
      },
    ) => {
      const nodeIdMapping: Record<string, string> = {}
      const hasImportedSystemPrompt = hasOwn(options, 'importedSystemPrompt')
      const importedSystemPrompt = options?.importedSystemPrompt

      // Validate nodes before making any canvas changes
      const invalidNode = nodesToProcess.find(
        (node) => !node.data || !node.data.meta || !node.data.config,
      )

      if (invalidNode) {
        logger.error(
          'Cannot load agent configuration with missing node data',
          { nodeId: invalidNode.id },
          { importedData: nodesToProcess },
        )
        errorToast({
          title: 'Cannot load agent configuration',
          description:
            'One or more nodes are missing required information. Please provide a valid agent configuration and try again.',
        })
        return
      }

      // If replacing all, clear existing nodes and edges first
      if (options?.replaceAll) {
        const existingNodes = getNodes()
        if (existingNodes.length > 0) {
          await deleteElements({ nodes: existingNodes })
        }
      }

      // Get all existing node names for conflict checking (empty set when replacing all)
      const existingNodeNames = options?.replaceAll
        ? new Set<string>()
        : new Set(
            getNodes()
              .map((node) => (node.data.config as { name?: string })?.name?.toLowerCase())
              .filter(Boolean),
          )
      const usedNamesInBatch = new Set<string>()

      const finalNodesToCreate: NodeType[] = nodesToProcess.map((node) => {
        const newId = uuidv4()
        nodeIdMapping[node.id] = newId

        const {
          triggers: _,
          position: __,
          ...nodeWithoutTriggersAndPosition
        } = node as NodeType & {
          triggers?: TriggerType[]
          position?: XYPosition
        }

        // Handle name conflicts
        let newName = (node.data?.config as { name?: string })?.name
        if (newName) {
          let counter = 1
          let tempName = newName
          while (
            existingNodeNames.has(tempName.toLowerCase()) ||
            usedNamesInBatch.has(tempName.toLowerCase())
          ) {
            tempName = `${newName} ${counter}`
            counter += 1
          }
          newName = tempName
          usedNamesInBatch.add(newName.toLowerCase())
        }

        return {
          ...nodeWithoutTriggersAndPosition,
          id: newId,
          data: {
            meta: node.data.meta,
            config: {
              ...normalizeImportedNodeConfig(node),
              name: newName || node.data.config.name,
            },
          },
          position: { x: 0, y: 0 }, // Temporary position, will be auto-arranged
          selected: false,
        } as NodeType
      })

      const finalEdgesToCreate: EdgeType[] = edgesToProcess
        .map((edge) => {
          const newSourceId = nodeIdMapping[edge.source]
          const newTargetId = nodeIdMapping[edge.target]
          if (newSourceId && newTargetId) {
            return {
              ...edge,
              id: uuidv4(),
              source: newSourceId,
              target: newTargetId,
              selected: true,
            } as EdgeType
          }
          return null
        })
        .filter((edge): edge is EdgeType => edge !== null)

      if (
        finalNodesToCreate.length === 0 &&
        finalEdgesToCreate.length === 0 &&
        !options?.replaceExistingStartNode // If replacing, an empty paste might still be a valid operation (to remove start node)
      ) {
        errorToast({
          title: 'Cannot paste agent configuration',
          description:
            'The agent configuration you are trying to paste is empty or invalid. Please copy valid configuration and try again.',
        })
        return
      }

      // If replacing start node, delete existing start nodes on canvas first
      if (options?.replaceExistingStartNode) {
        const existingStartNodes = getNodes().filter((n) => n.type === FlowNodeTypes.start)
        if (existingStartNodes.length > 0) {
          await deleteElements({ nodes: existingStartNodes })
        }
      }

      // Add the new nodes
      addNodes(finalNodesToCreate)

      // Set flow type immediately if we have a start node to ensure correct handle ID
      const pastedStartNode = finalNodesToCreate.find((n) => n.type === FlowNodeTypes.start)
      if (pastedStartNode) {
        const flowType = getPhloTypeFromStartNodeTrigger(pastedStartNode)
        if (flowType) {
          setCurrentFlowType(flowType)

          const { setAgentMeta } = useFlowStore.getState() // Update agentMeta with the correct default channel
          const defaultChannel = getChannelTypeFromFlowType(flowType)
          setAgentMeta({ defaultChannel })
        }
      }

      // Add edges after nodes are rendered - setTimeout ensures React Flow has processed nodes
      setTimeout(() => {
        addEdges(finalEdgesToCreate)

        // Layout and fit view after edges are added
        setTimeout(() => {
          const allNodes = getNodes()
          const allEdges = getEdges()

          // Save existing nodes' positions before layout
          const existingNodePositions = new Map(
            allNodes
              .filter((node) => !finalNodesToCreate.some((n) => n.id === node.id))
              .map((node) => [node.id, node.position]),
          )

          const { nodes: layoutedNodes } = getLayoutedElements(
            allNodes as GenericNode[],
            allEdges as GenericEdge[],
          )

          // Restore existing nodes' positions, only update new nodes
          const updatedNodes = layoutedNodes.map((node) => {
            const originalPosition = existingNodePositions.get(node.id)
            return originalPosition ? { ...node, position: originalPosition } : node
          })

          // Update node positions and fit view
          setNodes(updatedNodes as NodeType[])
          fitView({ maxZoom: layoutedNodes.length > 7 ? 0.8 : 1 })
        }, 50)
      }, 100)

      // Post-paste cleanup runs in parallel
      setTimeout(async () => {
        const currentNodesOnCanvas = getNodes()

        // Check if there's a start node on the canvas (pasted or existing)
        const hasStartNode =
          finalNodesToCreate.some(
            (n) =>
              n.type === FlowNodeTypes.start && currentNodesOnCanvas.some((c) => c.id === n.id),
          ) ||
          (!options?.replaceExistingStartNode &&
            currentNodesOnCanvas.some((n) => n.type === FlowNodeTypes.start))

        if (hasStartNode) {
          const triggerNodes = currentNodesOnCanvas.filter(
            (node) => node.type === FlowNodeTypes.select_trigger,
          )
          if (triggerNodes.length > 0) {
            await deleteElements({ nodes: triggerNodes })
          }
        }

        // Set system prompt with updated node IDs
        if (hasImportedSystemPrompt) {
          const { setSystemPrompt } = useFlowStore.getState()
          setSystemPrompt(
            importedSystemPrompt
              ? {
                  ...importedSystemPrompt,
                  ai_nodes: importedSystemPrompt.ai_nodes
                    .map((nodeId) => nodeIdMapping[nodeId])
                    .filter((nodeId): nodeId is string => Boolean(nodeId)),
                }
              : null,
            false,
          )
        }
      }, 200)
    },
    [
      addNodes,
      addEdges,
      getNodes,
      getEdges,
      deleteElements,
      setCurrentFlowType,
      getLayoutedElements,
      setNodes,
      fitView,
    ],
  )

  const handleIgnoreStartNodeAndPaste = useCallback(() => {
    if (!conflictedPasteData) return
    const { nodes, edges } = conflictedPasteData
    const filteredNodes = nodes.filter((node) => node.type !== FlowNodeTypes.start)
    const startNodeIdsToIgnore = nodes
      .filter((node) => node.type === FlowNodeTypes.start)
      .map((n) => n.id)

    const filteredEdges = edges.filter(
      (edge) =>
        !startNodeIdsToIgnore.includes(edge.source) && !startNodeIdsToIgnore.includes(edge.target),
    )

    applyPastedContent(filteredNodes, filteredEdges, {
      ...(hasOwn(conflictedPasteData, 'importedSystemPrompt')
        ? { importedSystemPrompt: conflictedPasteData.importedSystemPrompt ?? null }
        : {}),
    })
    setIsDuplicateStartNodeModalOpen(false)
    setConflictedPasteData(undefined)
  }, [conflictedPasteData, applyPastedContent])

  const handleReplaceStartNodeAndPaste = useCallback(() => {
    if (!conflictedPasteData) return
    applyPastedContent(conflictedPasteData.nodes, conflictedPasteData.edges, {
      replaceExistingStartNode: true,
      ...(hasOwn(conflictedPasteData, 'importedSystemPrompt')
        ? { importedSystemPrompt: conflictedPasteData.importedSystemPrompt ?? null }
        : {}),
    })
    setIsDuplicateStartNodeModalOpen(false)
    setConflictedPasteData(undefined)
  }, [conflictedPasteData, applyPastedContent])

  const handleCancelPaste = useCallback(() => {
    setIsDuplicateStartNodeModalOpen(false)
    setConflictedPasteData(undefined)
  }, [])

  const processAndPasteContent = useCallback(
    (
      nodesToPaste: NodeType[],
      edgesToPaste: EdgeType[],
      sourceDescription: string,
      metadata?: PasteMetadata,
    ) => {
      if (!isEditing) {
        errorToast({
          title: 'Import Restricted',
          description: 'Modifying the agent configuration is only allowed in edit mode.',
        })
        return
      }

      if (
        refuseSubAccountLockedNodes(nodesToPaste, sourceDescription === 'file' ? 'import' : 'paste')
      ) {
        return
      }

      if (nodesToPaste.length === 0 && edgesToPaste.length === 0) {
        errorToast({
          title: `Nothing to ${sourceDescription === 'file' ? 'import' : 'paste'}`,
          description:
            sourceDescription === 'file'
              ? 'The selected file is empty or contains invalid agent configuration.'
              : 'Make sure to copy some agent configuration first.',
        })
        return
      }

      // File import replaces entire canvas, no need for duplicate start node check
      if (sourceDescription === 'file') {
        applyPastedContent(nodesToPaste, edgesToPaste, {
          replaceAll: true,
          ...(hasOwn(metadata, 'importedSystemPrompt')
            ? { importedSystemPrompt: metadata?.importedSystemPrompt ?? null }
            : {}),
        })
      } else {
        const pastingStartNode = nodesToPaste.some((node) => node.type === FlowNodeTypes.start)
        if (pastingStartNode) {
          const existingStartNodeOnCanvas = getNodes().some(
            (node) => node.type === FlowNodeTypes.start,
          )
          if (existingStartNodeOnCanvas) {
            setConflictedPasteData({ nodes: nodesToPaste, edges: edgesToPaste, ...metadata })
            setIsDuplicateStartNodeModalOpen(true)
            return // Wait for user action from modal
          }
        }

        applyPastedContent(nodesToPaste, edgesToPaste, {
          ...(hasOwn(metadata, 'importedSystemPrompt')
            ? { importedSystemPrompt: metadata?.importedSystemPrompt ?? null }
            : {}),
        })
      }
      successToast({
        title: `Pasted content from ${sourceDescription}`,
        description: 'Please review the agent and make any necessary adjustments.',
      })
    },
    [
      isEditing,
      getNodes,
      applyPastedContent,
      setConflictedPasteData,
      setIsDuplicateStartNodeModalOpen,
    ],
  )

  // Helper to get selected nodes and their connected edges
  const getSelectedNodesAndEdges = useCallback(() => {
    const selectedNodes = getNodes().filter((node) => node.selected)
    const selectedEdges = getConnectedEdges(selectedNodes, getEdges()).filter((edge) => {
      const isExternalSource = selectedNodes.every((n) => n.id !== edge.source)
      const isExternalTarget = selectedNodes.every((n) => n.id !== edge.target)
      return !(isExternalSource || isExternalTarget)
    })
    return { selectedNodes, selectedEdges }
  }, [getNodes, getEdges])

  // Helper to write nodes/edges to clipboard
  const writeToClipboard = useCallback(
    async (nodes: NodeType[], edges: EdgeType[], action: 'copy' | 'cut') => {
      const copiedSourceHandles = new Set(
        edges
          .map((edge) => edge.sourceHandle)
          .filter((handle): handle is string => Boolean(handle)),
      )
      const filteredNodes = nodes.map((node) => removeOrphanIntents(node, copiedSourceHandles))
      const cleanedNodes = cleanNodesForExport(filteredNodes)
      const clipboardString = JSON.stringify({ nodes: cleanedNodes, edges }, null, 2)
      await navigator.clipboard.writeText(clipboardString)
      successToast({
        title: `${action === 'copy' ? 'Copied' : 'Cut'} agent configuration to clipboard`,
        description: 'Press Ctrl+V to paste the configuration into any agent.',
      })
    },
    [],
  )

  const copy = useCallback(async () => {
    // Don't hijack copy when the user isn't actually copying flow nodes. The global Meta/Ctrl+C shortcut
    // fires regardless of focus, so:
    //  - a form field (<input>/<textarea>/contentEditable) — its selection isn't in window.getSelection(),
    //  - OR focus inside a dialog/sheet (role="dialog"), e.g. the per-page preview sheet in the KB picker
    //    where the user selects + copies page-preview text,
    // would otherwise fall through to the "Nothing selected to copy" node error. Bail out and let the
    // browser perform its native copy of the user's text selection.
    const active = document.activeElement as HTMLElement | null
    if (
      active &&
      (active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable ||
        active.closest('[role="dialog"]'))
    ) {
      return
    }
    // Handle text selection in canvas
    const selection = window.getSelection()
    if (selection && selection.toString().length > 0) {
      try {
        navigator.clipboard.writeText(selection.toString())
      } catch (error) {
        logger.error('Error copying selected text to clipboard', error)
      }
      return
    }

    const { selectedNodes, selectedEdges } = getSelectedNodesAndEdges()

    if (selectedNodes.length === 0 && selectedEdges.length === 0) {
      errorToast({
        title: 'Nothing selected to copy',
        description: 'Select some agent nodes first.',
      })
      return
    }

    setBufferedNodes(selectedNodes)
    setBufferedEdges(selectedEdges)

    try {
      await writeToClipboard(selectedNodes, selectedEdges, 'copy')
    } catch (_error) {
      errorToast({ title: 'Error copying to clipboard', description: 'Please try again.' })
    }
  }, [getSelectedNodesAndEdges, writeToClipboard])

  const cut = useCallback(async () => {
    if (!isEditing) {
      errorToast({
        title: 'Edit mode required',
        description: 'Cutting is only allowed in edit mode.',
      })
      return
    }

    const { selectedNodes, selectedEdges } = getSelectedNodesAndEdges()
    if (selectedNodes.length === 0 && selectedEdges.length === 0) {
      errorToast({
        title: 'Nothing selected to cut',
        description: 'Select some agent nodes first.',
      })
      return
    }

    setBufferedNodes(selectedNodes)
    setBufferedEdges(selectedEdges)

    try {
      await writeToClipboard(selectedNodes, selectedEdges, 'cut')
      deleteElements({ nodes: selectedNodes, edges: selectedEdges })
    } catch (_error) {
      errorToast({ title: 'Error cutting to clipboard', description: 'Please try again.' })
    }
  }, [getSelectedNodesAndEdges, writeToClipboard, deleteElements, isEditing])

  const paste = useCallback(async () => {
    if (!isEditing) {
      errorToast({
        title: 'Paste Restricted',
        description: 'Pasting is only allowed in edit mode.',
      })
      return
    }

    let nodesToUseForPaste: NodeType[] = bufferedNodes
    let edgesToUseForPaste: EdgeType[] = bufferedEdges
    let sourceDescription = 'buffer'

    try {
      const clipboardText = await navigator.clipboard.readText()
      if (clipboardText) {
        const parsedJson = JSON.parse(clipboardText)
        const validationResult = reactFlowContentSchema.safeParse(parsedJson)
        if (validationResult.success) {
          const clipboardData = validationResult.data as ReactFlowDataObject<NodeType, EdgeType>
          nodesToUseForPaste = clipboardData.nodes
          edgesToUseForPaste = clipboardData.edges
          sourceDescription = 'clipboard'
        } else {
          errorToast({
            title: 'Invalid agent configuration in clipboard',
            description:
              'The content in your clipboard is not in the correct format for an agent. Please ensure you have copied valid agent configuration and try pasting again.',
          })
        }
      }
    } catch (_error) {
      // If clipboard fails, we stick with the internal buffer and its sourceDescription
    }

    processAndPasteContent(nodesToUseForPaste, edgesToUseForPaste, sourceDescription)
  }, [isEditing, bufferedNodes, bufferedEdges, processAndPasteContent])

  useShortcut(['Meta+x', 'Control+x'], cut)
  useShortcut(['Meta+c', 'Control+c'], copy)
  useShortcut(['Meta+v', 'Control+v'], paste)

  // Handle file import from navigation state
  useEffect(() => {
    const importedContent = location.state?.importedFlowContent as string | undefined
    const importId = location.state?.importId as number | undefined
    // isNew gates re-imports instead of a deferred state-clearing navigate: the first
    // save strips ?new and nulls location.state (usePhloMutation's replace-navigate),
    // so a refresh after saving can't re-import over the saved flow, while a refresh
    // BEFORE saving re-runs the import and restores the canvas. Same-session re-runs
    // are deduped by lastProcessedImportId. (SER-6079)
    if (
      !importedContent ||
      !isEditing ||
      !isNew ||
      (importId != null && lastProcessedImportId === importId)
    )
      return

    lastProcessedImportId = importId ?? null

    try {
      const validationResult = reactFlowContentSchema.safeParse(JSON.parse(importedContent))
      if (!validationResult.success) {
        errorToast({ title: 'Import Failed', description: 'Invalid file structure.' })
        return
      }

      const importedFlow = validationResult.data
      const { nodes, edges } = importedFlow as ReactFlowDataObject<NodeType, EdgeType>
      // Before the settings and metadata writes below, so a refused import changes nothing.
      if (refuseSubAccountLockedNodes(nodes, 'import')) return

      processAndPasteContent(nodes, edges, 'file', {
        ...(hasOwn(importedFlow, 'systemPrompt')
          ? { importedSystemPrompt: importedFlow.systemPrompt ?? null }
          : {}),
      })

      const importedAgentSettings = getImportedAgentSettings({
        ...importedFlow,
        agentSettings:
          importedFlow.agentSettings ||
          (location.state?.importedAgentSettings as AgentSettingsFormValues | null),
      })
      if (importedAgentSettings) {
        useFlowStore.getState().setAgentSettings(importedAgentSettings)
      }

      const { setSttGuidance, setAgentName, setAgentPersona } = useFlowStore.getState()
      if (hasOwn(importedFlow, 'sttGuidance')) setSttGuidance(importedFlow.sttGuidance ?? null)
      if (hasOwn(importedFlow, 'agentName')) {
        setAgentName(importedFlow.agentName ?? null)
      }
      if (hasOwn(importedFlow, 'agentPersona')) {
        setAgentPersona(importedFlow.agentPersona ?? null)
      }
    } catch {
      errorToast({ title: 'Import Failed', description: 'Could not parse the file.' })
    }
  }, [location, processAndPasteContent, isEditing, isNew])

  return {
    cut,
    copy,
    paste,
    bufferedNodes,
    bufferedEdges,
    isDuplicateStartNodeModalOpen,
    duplicateStartNodeModalActions: {
      onIgnoreAndPaste: handleIgnoreStartNodeAndPaste,
      onReplaceAndPaste: handleReplaceStartNodeAndPaste,
      onCancel: handleCancelPaste,
    },
    pasteContent: processAndPasteContent,
  }
}
